/**
 * Detects when the React app is running inside the iOS native wrapper
 * (WKWebView + JS bridge). Also provides a helper to trigger native
 * Apple In-App Purchase from JS.
 *
 * How iOS opts in (Swift side):
 *   1. On WKWebView creation, inject at .atDocumentStart:
 *        window.__CFF_NATIVE__ = { platform: "ios", version: "1.0.0" };
 *      via WKUserScript. This MUST run before React mounts.
 *   2. Register a WKScriptMessageHandler named "iap" that receives
 *      { action, ... } messages and dispatches on `action`:
 *        - "purchase" → { tier, billing, productId } — invoke StoreKit
 *          Product.purchase(...), then call window.__CFF_ON_IAP_RESULT__
 *          with { status: "success" | "cancelled" | "failed", ... }.
 *        - "restore" → iterate Transaction.currentEntitlements, then call
 *          window.__CFF_ON_IAP_RESULT__({
 *              status: "restored",
 *              transactions: [{ transactionId, productId }, ...]
 *          }) OR { status: "nothing-to-restore" } when empty.
 *   3. After purchase / restore, Swift calls window.__CFF_ON_IAP_RESULT__(payload)
 *      (or evaluateJavaScript) so the web app can POST /api/iap/verify-ios
 *      and refresh /auth/me.
 *
 * Because Swift injection can race with React's initial render, the
 * `useNativeBridge` hook polls for the bridge for up to 2 seconds and
 * re-renders when it becomes available.
 */

import { useEffect, useState } from "react";

const _hasWKIapHandler = () =>
  typeof window !== "undefined" &&
  !!(window.webkit &&
    window.webkit.messageHandlers &&
    window.webkit.messageHandlers.iap);

const _hasCffFlag = (platform) =>
  typeof window !== "undefined" &&
  !!(window.__CFF_NATIVE__ && window.__CFF_NATIVE__.platform === platform);

const _uaLooksLikeiOSWebView = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // WKWebView on iOS: has "AppleWebKit" + "Mobile" but no "Safari" token
  // (real mobile Safari includes "Safari/"). This heuristic is only used
  // as a diagnostic — never to route real purchases.
  const iosLike = /iPhone|iPad|iPod/i.test(ua);
  const noSafariToken = !/Safari\//i.test(ua);
  return iosLike && noSafariToken;
};

export const isIOSNative = () => {
  if (typeof window === "undefined") return false;
  if (_hasCffFlag("ios")) return true;
  if (_hasWKIapHandler()) return true;
  return false;
};

export const isAndroidNative = () => {
  if (typeof window === "undefined") return false;
  if (_hasCffFlag("android")) return true;
  if (typeof window.AndroidBilling === "object") return true;
  return false;
};

/**
 * React hook — reactive to late bridge injection. Polls every 100ms for
 * up to 2s so Swift-side WKUserScript races with React don't leave us
 * thinking we're in a web browser.
 */
export const useNativeBridge = () => {
  const [state, setState] = useState({
    ios: isIOSNative(),
    android: isAndroidNative(),
    iosLike: _uaLooksLikeiOSWebView(),
    // Diagnostic: true when UA screams iOS-webview but the bridge flag/handler is missing.
    iosLikelyBroken: false,
  });

  useEffect(() => {
    if (state.ios || state.android) return; // already detected
    let attempts = 0;
    const iv = setInterval(() => {
      attempts += 1;
      const ios = isIOSNative();
      const android = isAndroidNative();
      const iosLike = _uaLooksLikeiOSWebView();
      if (ios || android) {
        setState({ ios, android, iosLike, iosLikelyBroken: false });
        clearInterval(iv);
        return;
      }
      if (attempts >= 20) {
        // 2s elapsed, bridge never showed up.
        setState({
          ios: false,
          android: false,
          iosLike,
          iosLikelyBroken: iosLike, // UA says iOS-webview but bridge missing → misconfigured wrapper
        });
        clearInterval(iv);
      }
    }, 100);
    return () => clearInterval(iv);
  }, []);

  return state;
};

/**
 * Product ID naming differs by store:
 *   iOS (App Store Connect):  com.careforflora.mobile.<tier>.<billing>
 *   Android (Google Play):    com.careforflora.app.<tier>.<billing>
 * The backend substring-matches on "<tier>.<billing>" so both prefixes
 * resolve to the same server-side tier mapping.
 */
const IOS_PRODUCT_PREFIX = "com.careforflora.mobile";
const ANDROID_PRODUCT_PREFIX = "com.careforflora.app";

export const buildProductId = ({ platform, tier, billing }) => {
  const prefix = platform === "android" ? ANDROID_PRODUCT_PREFIX : IOS_PRODUCT_PREFIX;
  return `${prefix}.${tier}.${billing}`;
};

/**
 * Trigger a native IAP purchase from JS.
 * Returns true when the bridge was invoked; false when no native bridge
 * exists (caller should fall back to Stripe OR surface an error).
 */
export const triggerNativePurchase = ({ tier, billing }) => {
  try {
    if (_hasWKIapHandler()) {
      const productId = buildProductId({ platform: "ios", tier, billing });
      const payload = { action: "purchase", tier, billing, productId };
      console.log("[CFF] Invoking iOS StoreKit bridge with", payload);
      window.webkit.messageHandlers.iap.postMessage(payload);
      return true;
    }
    if (window.AndroidBilling && typeof window.AndroidBilling.purchase === "function") {
      const productId = buildProductId({ platform: "android", tier, billing });
      console.log("[CFF] Invoking Android Billing bridge with", productId);
      window.AndroidBilling.purchase(productId);
      return true;
    }
  } catch (e) {
    console.warn("[CFF] Native bridge invocation failed:", e);
  }
  return false;
};

/**
 * Trigger a native "Restore Purchases" flow. Swift should iterate
 * `Transaction.currentEntitlements` and call
 *   window.__CFF_ON_IAP_RESULT__({
 *     status: "restored",
 *     transactions: [{ transactionId, productId }, ...],
 *   })
 * or `{ status: "nothing-to-restore" }` when the user has no active
 * subscription tied to this Apple ID. Returns true when the bridge was
 * invoked; false when no native bridge exists.
 */
export const triggerNativeRestore = () => {
  const payload = { action: "restore" };
  try {
    if (_hasWKIapHandler()) {
      console.log("[CFF] Invoking iOS StoreKit restore bridge");
      window.webkit.messageHandlers.iap.postMessage(payload);
      return true;
    }
    if (window.AndroidBilling && typeof window.AndroidBilling.restore === "function") {
      console.log("[CFF] Invoking Android Billing restore bridge");
      window.AndroidBilling.restore();
      return true;
    }
  } catch (e) {
    console.warn("[CFF] Native restore invocation failed:", e);
  }
  return false;
};

/**
 * Global callback invoked BY THE SWIFT SIDE after a purchase completes
 * (successful or cancelled). Wire this from your app root:
 *
 *   window.__CFF_ON_IAP_RESULT__ = (payload) => { ... }
 *
 * Payload shape:
 *   { status: "success" | "cancelled" | "failed", transactionId?, productId?, error? }
 */
export const registerIapResultHandler = (handler) => {
  if (typeof window === "undefined") return () => {};
  window.__CFF_ON_IAP_RESULT__ = handler;
  return () => {
    if (window.__CFF_ON_IAP_RESULT__ === handler) {
      delete window.__CFF_ON_IAP_RESULT__;
    }
  };
};
